import { Injectable } from '@angular/core';
import { User, UserManager } from 'oidc-client-ts';
import { jwtDecode } from 'jwt-decode';
import { environment } from '../../../environments/environment';
import { SUPER_ADMIN, HOST_AGENT, ADMIN, CORPORATE_ADMIN } from '../constants';
import { from, Observable } from 'rxjs';
import { Primitive } from 'lodash';
import { AgentModel } from '../models/auth.model';
import { LocationStateService } from './location-state.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  userManager: UserManager;

  constructor(private locationState: LocationStateService) {
    const settings = {
      authority: environment.STS_AUTHORITY,
      client_id: environment.CLIENT_ID,
      redirect_uri: `${environment.CLIENT_ROOT}signin-callback`,
      silent_redirect_uri: `${environment.CLIENT_ROOT}silent-callback.html`,
      post_logout_redirect_uri: `${environment.CLIENT_ROOT}`,
      response_type: 'code',
      scope: environment.CLIENT_SCOPE,
    };
    this.userManager = new UserManager(settings);
  }

  getUser(): Promise<User | null> {
    return this.userManager.getUser();
  }

  login(): Promise<void> {
    const userLanguage = localStorage.getItem("ppuLanguagePreference");
    return this.userManager.signinRedirect({extraQueryParams: {culture: userLanguage ?? 'en'}});
  }

  renewToken(): Promise<User | null> {
    return this.userManager.signinSilent();
  }

  logout(): Promise<void> {
    this.locationState.clearLocationId();
    return this.userManager.signoutRedirect();
  }

  getAccessToken(): Observable<string> {
    let token = '';
    const observable$ = from(
      this.getUser().then((user) => {
        if (user) {
          token = user.access_token;
        }
        return token;
      })
    );
    return observable$;
  }

  getUserRoles(): Promise<Array<string>> {
    let roles = Array<string>();
    return this.getUser().then((user) => {
      if (user) {
        const decoded: any = jwtDecode(user.access_token);
        if (Array.isArray(decoded.role)) {
          roles = decoded.role;
        }
        roles.push(decoded.role);
      }
      return roles;
    });
  }

  async isValidUserRole(): Promise<boolean> {
    let result = false;
    const roles = await this.getUserRoles();
    if (roles && roles.length > 0 && Array.isArray(roles)) {
      result = roles.some((r) => {
        return r === SUPER_ADMIN || r === HOST_AGENT || r === ADMIN || r === CORPORATE_ADMIN;
      });
    }
    return result;
  }

  isAdminRole(): Observable<boolean> {
    let result = false;
    const observable$ = from(
      this.getUserRoles().then((roles) => {
        if (roles && roles.length > 0 && Array.isArray(roles)) {
          result = roles.some((r) => {
            return r === SUPER_ADMIN || r === ADMIN || r === CORPORATE_ADMIN;
          });
        }
        return result;
      })
    );
    return observable$;
  }

  getAgentInfo(): Promise<AgentModel> {
    let agent = {} as AgentModel;
    return this.getUser().then((user) => {
      if (user) {
        const decoded: any = jwtDecode(user.access_token);
        if (Array.isArray(decoded.role)) {
          agent.role = (<Array<string>>decoded.role).filter((r) =>
            r === SUPER_ADMIN || r === ADMIN || r === CORPORATE_ADMIN
          )[0];
        } else {
          agent.role = decoded.role;
        }
        agent.firstName = decoded.firstName;
        agent.lastName = decoded.lastName;
        agent.email = decoded.email;
      }
      return agent;
    });
  }
}
